"use client";

import { useEffect, useRef } from "react";
import { useZStore } from "@/utils/z-store";
import { useSession } from "next-auth/react";
import { useSearchParams } from "next/navigation";
import { FaMailchimp } from "react-icons/fa";
import TextComponent from "@/app/_components/TextComponent";

/**
 * MailchimpApiKeyChecker
 *
 * This component checks every 60 seconds if the user has usable Mailchimp data.
 * It opens the mailchimp-api-key modal in the following cases:
 * 1. No API key is found
 * 2. API key exists but the connected Mailchimp account has no processed data (no campaigns)
 *
 * This ensures users are prompted to either add a key or connect an account that has data.
 */
export default function MailchimpApiKeyChecker() {
  const { data, actions } = useZStore();
  const { status } = useSession();
  const searchParams = useSearchParams();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Track when we've found valid data to stop further checks
  const hasValidDataRef = useRef(false);

  // Check if user is coming from Mailchimp OAuth flow
  const fromMailchimp = searchParams.get("fromMailchimp") === "true";

  // Re-arm the checker whenever the active workspace changes. This component is
  // mounted in the layout (OUTSIDE the WorkspaceBoundary), so it does not
  // remount on a switch; without this reset the "valid data" latch from the
  // previous workspace would wrongly suppress the check for the new one (and,
  // conversely, keep prompting after switching into a workspace that has a key).
  useEffect(() => {
    hasValidDataRef.current = false;
  }, [data.activeWorkspaceId]);

  useEffect(() => {
    // Skip check during Mailchimp OAuth flow - let welcome modal handle it
    if (fromMailchimp) {
      console.log("[MailchimpApiKeyChecker] Skipping - user is in Mailchimp OAuth flow.");
      return;
    }

    // Only run if user is authenticated and not in loading state
    if (
      status !== "authenticated" ||
      data.loading?.isInitialLoading ||
      data.loading?.isLoading
    ) {
      return;
    }

    // Function to check if user has Mailchimp API keys with data
    // This uses the latest state from the store via closure
    const checkMailchimpApiKey = () => {
      // If we've already found valid data, don't check again
      if (hasValidDataRef.current) {
        console.log("[MailchimpApiKeyChecker] Already has valid data, skipping check.");
        return;
      }

      console.log("[MailchimpApiKeyChecker] Running check...");
      const currentData = useZStore.getState().data;
      const mailchimpProfile = currentData?.mailchimpProfile;
      const mailchimpKeys = mailchimpProfile?.mailchimpKeys || [];
      const processedData = currentData?.processedData;
      const processingStatus = currentData?.processingStatus;
      const loadingState = currentData?.loading;

      // Don't show modal if any loading is in progress
      if (loadingState?.isLoading || loadingState?.isInitialLoading) {
        console.log("[MailchimpApiKeyChecker] Loading in progress, skipping check.");
        return;
      }

      // Don't show modal if processing data or recently completed (give time for data to populate)
      if (
        processingStatus?.status === "processing" ||
        processingStatus?.status === "completed"
      ) {
        console.log("[MailchimpApiKeyChecker] Processing in progress or just completed, skipping check.");
        return;
      }

      // Don't show modal if it was recently closed by the user
      if (currentData.modal.mailchimpApiKeyModalClosed) {
        console.log("[MailchimpApiKeyChecker] Modal was closed by user, skipping auto-open.");
        return;
      }

      // Don't judge key state until the ACTIVE workspace's Mailchimp profile has
      // actually loaded. The workspace-scoped profile is fetched by the home page
      // (which sets `userId` alongside it); on a workspace switch,
      // resetWorkspaceScopedData clears both `userId` and `mailchimpKeys`. Until
      // the reload completes, empty keys mean "not fetched yet", NOT "no key" —
      // firing Case 1 here wrongly prompts users whose workspace HAS a key.
      if (!currentData.userId) {
        console.log(
          "[MailchimpApiKeyChecker] Active workspace profile not loaded yet, skipping check.",
        );
        return;
      }

      // Case 1: No API keys at all
      if (mailchimpKeys.length === 0) {
        if (!currentData.modal.isOpen) {
          console.log("[MailchimpApiKeyChecker] No API keys found. Opening modal.");
          actions.openModal({
            modalName: "mailchimp-api-key",
            modalProps: { reason: "no-keys" }
          });
        }
        return;
      }

      // Case 2: Has API keys - check if processedData has been fetched yet
      // When data is loaded, processedData will have keyId property
      // If processedData is empty or has no keyId, data is still loading
      const isProcessedDataLoaded = processedData?.keyId || processedData?._id;
      if (!isProcessedDataLoaded) {
        console.log("[MailchimpApiKeyChecker] ProcessedData not loaded yet, skipping check.");
        return;
      }

      // Case 3: Has API keys and processedData loaded, but check if there's actual data
      // Check if there's any actual data by looking at:
      // - allEmails.allEmails.totalSent
      // - campaignsData (object with data)
      // - automationData (object with data)
      // Only show modal if ALL of them are empty/0
      const totalSent = processedData?.allEmails?.allEmails?.totalSent ?? 0;
      const hasCampaignData = processedData?.campaignsData &&
        typeof processedData.campaignsData === 'object' &&
        Object.keys(processedData.campaignsData).length > 0;
      const hasAutomationData = processedData?.automationData &&
        typeof processedData.automationData === 'object' &&
        Object.keys(processedData.automationData).length > 0;

      const hasAnyData = totalSent > 0 || hasCampaignData || hasAutomationData;

      if (!hasAnyData) {
        if (!currentData.modal.isOpen) {
          console.log("[MailchimpApiKeyChecker] API key exists but no data (totalSent=0, no campaigns, no automations). Opening modal.");
          actions.openModal({
            modalName: "mailchimp-api-key",
            modalProps: { reason: "no-data" }
          });
        }
        return;
      }

      // Mark that we have valid data to prevent future checks
      hasValidDataRef.current = true;
      console.log("[MailchimpApiKeyChecker] User has API keys with data. All good. Stopping future checks.");

      // Clear the interval since we have valid data
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    // Run the check immediately on mount
    checkMailchimpApiKey();

    // Set up interval to check every 60 seconds (60000 milliseconds)
    // Only if we haven't already found valid data
    if (!hasValidDataRef.current) {
      intervalRef.current = setInterval(() => {
        checkMailchimpApiKey();
      }, 60000 * 5); // 5 minutes
    }

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    status,
    data.loading?.isInitialLoading,
    data.loading?.isLoading,
    data.userId,
    data.mailchimpProfile?.mailchimpKeys,
    actions,
    fromMailchimp,
  ]);

  const handleOpenModal = () => {
    actions.setMailchimpApiKeyModalClosed(false);
    actions.openModal({
      modalName: "mailchimp-api-key",
      modalProps: { reason: "no-keys" }
    });
  };

  // Drive the reminder chip off the CURRENT workspace's loaded render state —
  // NOT `hasValidDataRef` (a ref that's reset on every switch and never triggers
  // a re-render) and NOT the persisted, global `mailchimpApiKeyModalClosed`
  // alone. Because the "modal closed" flag is sticky across all workspaces once
  // dismissed, the old check showed the chip for every workspace regardless of
  // whether it actually had a key.
  //
  // Only remind once the active workspace's profile has loaded (userId set
  // alongside the scoped profile). The workspace needs attention when it has no
  // key, or has a key whose data has loaded but is empty. While a keyed
  // workspace's data is still loading, we don't know yet — so we don't nag.
  const profileLoaded = !!data.userId;
  const mailchimpKeys = data.mailchimpProfile?.mailchimpKeys || [];
  const hasKeys = mailchimpKeys.length > 0;

  const processedData = data.processedData;
  const processedLoaded = !!(processedData?.keyId || processedData?._id);
  const totalSent = processedData?.allEmails?.allEmails?.totalSent ?? 0;
  const hasCampaignData =
    !!processedData?.campaignsData &&
    typeof processedData.campaignsData === "object" &&
    Object.keys(processedData.campaignsData).length > 0;
  const hasAutomationData =
    !!processedData?.automationData &&
    typeof processedData.automationData === "object" &&
    Object.keys(processedData.automationData).length > 0;
  const hasAnyData = totalSent > 0 || hasCampaignData || hasAutomationData;

  const workspaceNeedsSetup =
    profileLoaded && (!hasKeys || (processedLoaded && !hasAnyData));

  const showChip =
    workspaceNeedsSetup &&
    data.modal.mailchimpApiKeyModalClosed &&
    !data.modal.isOpen;

  if (!showChip) return null;

  return (
    <div
      onClick={handleOpenModal}
      className="fixed bottom-0 left-0 right-0 z-[51] flex cursor-pointer items-center justify-center gap-2.5 rounded-t-[12px] bg-[#FFDD22] px-4 py-2 shadow-lg transition-all duration-300 ease-in-out hover:pb-3 hover:shadow-xl md:left-auto md:right-4 md:mr-72 md:w-auto md:justify-start"
      style={{
        animation: "chipSlideIn 0.3s ease-out forwards",
      }}
    >
      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-black/10">
        <FaMailchimp className="h-4 w-4 text-black" />
      </div>

      <div className="flex items-center gap-1.5">
        <TextComponent type="caption" className="font-bold uppercase tracking-wider text-black opacity-70">
          Action Required
        </TextComponent>
        <TextComponent type="h4" className="text-black" style={{ fontWeight: 700, fontSize: "14px" }}>
          {hasKeys ? "Check Data" : "Connect Mailchimp"}
        </TextComponent>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes chipSlideIn {
          0% {
            transform: translateY(100%);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

